import apiClient from "../lib/apiClient";
import type { MemberResponse } from "./membersApi";
import type { AddressDto, AddressResponse, PageResponse } from "./types";

export interface ParcoursDateDto {
  /** Start date of the slot (YYYY-MM-DD). Required. */
  date: string;
  startTime?: string | null;
  /** End date (YYYY-MM-DD). Null ⇒ single-day slot. */
  endDate?: string | null;
  endTime?: string | null;
}

export interface PracticalInfoDto {
  title: string;
  description?: string;
  items?: string[];
}

export interface SponsorDto {
  name: string;
  imageUrl?: string;
  link?: string;
}

export interface ProgrammeInlineDto {
  title: string;
  subtitle?: string;
  date?: string;
  time?: string;
  endDate?: string | null;
  endTime?: string | null;
  address?: AddressDto;
  addressName?: string;
  lieuId?: string;
  imageLink?: string;
  price?: number;
}

export type ParcoursContentMode = "PROGRAMME" | "DESCRIPTION";

export interface ParcoursDto {
  title: string;
  description?: string;
  longDescription?: string;
  contentMode?: ParcoursContentMode;
  imageLink?: string;
  dates?: ParcoursDateDto[];
  lieuIds?: string[];
  artistIds?: string[];
  practicalInfos?: PracticalInfoDto[];
  resources?: string[];
  sponsors?: SponsorDto[];
  programmes?: ProgrammeInlineDto[];
  country?: string;
  region?: string;
  mainCommune?: string;
  accessibility?: string;
  contact?: string;
  secondaryContact?: string;
  website?: string;
  price?: number;
  paid?: boolean;
}

export interface ParcoursDateResponse {
  id: string;
  date: string;
  startTime?: string | null;
  endDate?: string | null;
  endTime?: string | null;
}

export interface PracticalInfoResponse {
  id: string;
  title: string;
  description?: string;
  items?: string[];
}

export interface SponsorResponse {
  id: string;
  name: string;
  imageUrl?: string;
  link?: string;
}

export interface LieuMiniResponse {
  id: string;
  name: string;
  description?: string;
  addressName?: string;
  address?: AddressResponse;
}

export interface ArtistMiniResponse {
  id: string;
  name: string;
  description?: string;
  photoLink?: string;
}

export interface ProgrammeMiniResponse {
  id: string;
  title: string;
  subtitle?: string;
  date?: string;
  endDate?: string;
  time?: string;
  endDate?: string | null;
  endTime?: string | null;
  address?: AddressResponse;
  addressName?: string;
  lieu?: LieuMiniResponse;
  imageLink?: string;
  price?: number;
  tag?: string;
}

export interface ParcoursResponse {
  id: string;
  tenantId: string;
  title: string;
  description?: string;
  longDescription?: string;
  contentMode?: ParcoursContentMode;
  imageLink?: string;
  dates: ParcoursDateResponse[];
  lieux: LieuMiniResponse[];
  artists: ArtistMiniResponse[];
  practicalInfos: PracticalInfoResponse[];
  resources: string[];
  sponsors: SponsorResponse[];
  programmes: ProgrammeMiniResponse[];
  country?: string;
  region?: string;
  mainCommune?: string;
  accessibility?: string;
  contact?: string;
  secondaryContact?: string;
  website?: string;
  price?: number;
  paid?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ParcoursSearchParams {
  search?: string;
  country?: string;
  region?: string;
  commune?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export async function listParcours(params: ParcoursSearchParams) {
  const { data } = await apiClient.get<PageResponse<ParcoursResponse>>("/parcours", { params });
  return data;
}

export async function getParcours(id: string) {
  const { data } = await apiClient.get<ParcoursResponse>(`/parcours/${id}`);
  return data;
}

export async function createParcours(dto: ParcoursDto) {
  const { data } = await apiClient.post<ParcoursResponse>("/parcours", dto);
  return data;
}

export async function updateParcours(id: string, dto: ParcoursDto) {
  const { data } = await apiClient.put<ParcoursResponse>(`/parcours/${id}`, dto);
  return data;
}

export async function deleteParcours(id: string) {
  await apiClient.delete(`/parcours/${id}`);
}

// ── Members assigned to a parcours ─────────────────────────────────────────

export async function listParcoursMembers(parcoursId: string) {
  const { data } = await apiClient.get<MemberResponse[]>(`/parcours/${parcoursId}/members`);
  return data;
}

export async function assignParcoursMember(parcoursId: string, memberId: string) {
  const { data } = await apiClient.post<MemberResponse>(`/parcours/${parcoursId}/members/${memberId}`);
  return data;
}

export async function unassignParcoursMember(parcoursId: string, memberId: string) {
  const { data } = await apiClient.delete<MemberResponse>(`/parcours/${parcoursId}/members/${memberId}`);
  return data;
}

export async function uploadParcoursImage(id: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await apiClient.post<string>(`/parcours/${id}/upload/image`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}
